<template>
  <div
    v-loading="isLoading"
    class="cloud-intercom-page ci-verification"
  >
    <div class="ci-body ci-body--center">
      <h1 class="ci-body__title">
        {{ $t('Intercom.CloudIntercom.VerificationPage.Title') }}
      </h1>
      <p class="ci-verification__hint">
        {{ $t('Intercom.CloudIntercom.VerificationPage.Label') }}
      </p>

      <!-- PIN 格子 -->
      <div class="ci-pin__wrapper">
        <div class="ci-pin">
          <div
            v-for="(digit, i) in digits"
            :key="i"
            class="ci-pin__box"
            :class="{
              'ci-pin__box--active': activeIndex === i && !hasError,
              'ci-pin__box--error': hasError
            }"
          >
            {{ digit }}
          </div>
        </div>
        <!-- 行內錯誤訊息 -->
        <div class="ci-pin__error-wrapper">
          <template v-if="hasError">
            <p>{{ $t('Intercom.CloudIntercom.VerificationPage.InvalidCodeTitle') }}</p>
            <p>{{ $t('Intercom.CloudIntercom.VerificationPage.InvalidCodeMsg') }}</p>
          </template>
        </div>
      </div>

      <!-- 現場登記連結 -->
      <div class="ci-link">
        {{ $t('Intercom.CloudIntercom.VerificationPage.NoCodePrefix') }}<span class="ci-link__emphasis" @click="$emit('go-registration')">{{ $t('Intercom.CloudIntercom.VerificationPage.GoOnSite') }}</span>
      </div>
    </div>

    <!-- 數字鍵盤 -->
    <NumberKeyboard @key="onKey" />
    <!-- reCAPTCHA 免責聲明（徽章已隱藏，依 Google 政策須顯示此文字） -->
    <p class="ci-recaptcha-disclaimer">
      This site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      and
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      apply.
    </p>
  </div>
</template>

<script>
import { apiVoip } from '@/api/Voip'
import NumberKeyboard from './components/NumberKeyboard.vue'

export default {
  name: 'VerificationPage',
  components: { NumberKeyboard },
  props: {
    geolocationToken: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      // 6 個格子的數字，空字串代表尚未輸入
      digits: ['', '', '', '', '', ''],
      // 目前 active（下一個輸入位置）的格子索引
      activeIndex: 0,
      isLoading: false,
      hasError: false
    }
  },
  computed: {
    verificationCode () {
      return this.digits.join('')
    }
  },
  methods: {
    /**
     * 數字鍵盤按鍵事件
     * @param {string} key - '0'~'9' | 'del' | 'cancel'
     */
    onKey (key) {
      if (this.isLoading) return
      if (key === 'cancel') {
        this.$router.go(-1)
      } else if (key === 'del') {
        if (this.digits[this.activeIndex] !== '') {
          this.$set(this.digits, this.activeIndex, '')
        } else if (this.activeIndex > 0) {
          this.activeIndex--
          this.$set(this.digits, this.activeIndex, '')
        }
        this.hasError = false
      } else {
        this.$set(this.digits, this.activeIndex, key)
        if (this.activeIndex < 5) this.activeIndex++
        this.hasError = false
        if (this.digits.every(d => d !== '')) this.submit()
      }
    },

    /** 送出驗證碼，成功後 emit arrived-by-code，失敗顯示行內錯誤 */
    async submit () {
      if (this.isLoading || this.verificationCode.length < 6) return
      this.isLoading = true
      this.hasError = false
      try {
        await this.$recaptchaLoaded()
        const recaptchaToken = await this.$recaptcha('guest_arrival_by_verification_code')
        const res = await apiVoip.Guest.ArrivedInfo({
          GeolocationToken: this.geolocationToken,
          VerificationCode: this.verificationCode,
          RecaptchaToken: recaptchaToken
        })
        this.$emit('arrived-by-code', { guestToken: res.data.GuestToken })
      } catch {
        this.hasError = true
        this.digits = ['', '', '', '', '', '']
        this.activeIndex = 0
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './cloudIntercom';

.ci-verification {
  display: flex;
  flex-direction: column;

  &__hint {
    font-size: map-get($fontSize,medium);
    color: #999;
    text-align: center;
    margin: 44px 0;
  }
}

.ci-body {
  padding-bottom: 8px;

  &__title {
    text-align: left;
  }
}

// PIN 格子區塊
.ci-pin__wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.ci-pin {
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;

  &__box {
    width: 38px;
    height: 46px;
    border: 1px solid #f3f3f3;
    border-radius: 8px;
    background-color: #f3f3f3;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: map-get($fontSize,xxx-large);
    font-weight: 600;
    line-height: 1.25;
    color: #1f211f;
    transition: border-color 0.15s;

    &--error {
      border-color: #eb3e1b;
    }
  }

  &__error-wrapper {
    height: 40px;
    margin-top: 12px;
    margin-bottom: 47px;

    p {
      margin: 0;
      line-height: 1.4;
      text-align: left;
      color: #eb3e1b;
      font-size: map-get($fontSize,x-small);
    }
  }
}

.ci-link {
  text-align: center;
  margin-bottom: 32px;

  &__emphasis {
    color: $mqFooter;
  }
}
</style>
